import { useContext, useMemo } from 'react';
import { CardChannelContext } from '@shared/scenes/cards/context';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';

export interface TagOption {
  id: string;
  value: string;
  label: string;
  backgroundColor: string;
  archived: boolean;
  fieldName: string;
  sortKey: string;
}

export interface UseTagOptionsResponse {
  options: TagOption[];
  loading: boolean;
}

export function useWorkflowTagOptions(componentId: string): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const { workflowTemplate, loading } = useWorkflowTemplate(channel?.workflowTemplateId);

  const options = useMemo(() => {
    const component = workflowTemplate?.components?.find(
      (c): c is API.TagComponent => isTagComponent(c) && c.id === componentId,
    );
    return (
      component?.options?.map((option): TagOption => ({
        id: option.value,
        value: option.value,
        label: option.label,
        backgroundColor: option.backgroundColor ?? '',
        archived: option.archived ?? false,
        fieldName: componentId,
        sortKey: option.value,
      })) ?? []
    );
  }, [workflowTemplate, componentId]);

  return { options, loading };
}

export function useCombinedTagOptions(componentIds: string[]): UseTagOptionsResponse {
  const channel = useContext(CardChannelContext);
  const { workflowTemplate, loading } = useWorkflowTemplate(channel?.workflowTemplateId);

  const options = useMemo(() => {
    return componentIds.flatMap(componentId => {
      const component = workflowTemplate?.components?.find(
        (c): c is API.TagComponent => isTagComponent(c) && c.id === componentId,
      );
      return (
        component?.options?.map((option): TagOption => ({
          id: option.value,
          value: option.value,
          label: option.label,
          backgroundColor: option.backgroundColor ?? '',
          archived: option.archived ?? false,
          fieldName: componentId,
          sortKey: option.value,
        })) ?? []
      );
    });
  }, [workflowTemplate, componentIds]);

  return { options, loading };
}
